import styles from './CreateProductForm.scss';
import { Dropdown, Input } from 'components';
import {
  MarkupAmountType,
  TaxAmountType,
} from 'lane-shared/domains/billingPayments/types';
import { AdminFeeAmountType } from 'application-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  isNumberTwoDecimalPlaces,
  isNumberThreeDecimalPlaces,
} from '../helpers/isNumberTwoDecimalPlaces';
import { SupportedPaymentCurrency } from 'domains-billing-currency';

const MARKUP_DROPDOWN_OPTIONS = [
  {
    label: 'abp.productsServices.createProduct.optionalRateType.fixed',
    value: MarkupAmountType.MarkupAmountTypeFixed,
  },
  {
    label: 'abp.productsServices.createProduct.optionalRateType.percentage',
    value: MarkupAmountType.MarkupAmountTypePercentage,
  },
];

const ADMIN_FEE_DROPDOWN_OPTIONS = [
  {
    label: 'abp.productsServices.createProduct.optionalRateType.fixed',
    value: AdminFeeAmountType.AdminFeeAmountTypeFixed,
  },
  {
    label: 'abp.productsServices.createProduct.optionalRateType.percentage',
    value: AdminFeeAmountType.AdminFeeAmountTypePercentage,
  },
];

const TAX_DROPDOWN_OPTIONS = [
  {
    label: 'abp.productsServices.createProduct.optionalRateType.fixed',
    value: TaxAmountType.TaxAmountTypeFixed,
  },
  {
    label: 'abp.productsServices.createProduct.optionalRateType.percentage',
    value: TaxAmountType.TaxAmountTypePercentage,
  },
];

export enum ModifierType {
  MARKUP = 'markup',
  ADMIN_FEE = 'adminFee',
  ADMIN_FEE_TAX = 'adminFeeTax',
  TAX = 'tax',
  RATE = 'rate',
}

const DROPDOWN_OPTIONS: Record<string, { label: string; value: string }[]> = {
  [ModifierType.MARKUP]: MARKUP_DROPDOWN_OPTIONS,
  [ModifierType.ADMIN_FEE]: ADMIN_FEE_DROPDOWN_OPTIONS,
  [ModifierType.ADMIN_FEE_TAX]: TAX_DROPDOWN_OPTIONS,
  [ModifierType.TAX]: TAX_DROPDOWN_OPTIONS,
};

interface Props {
  modifierType?: TaxAmountType | MarkupAmountType | AdminFeeAmountType;
  modifierValue?: string;
  updateDetails: (value: any, field: string) => void;
  type: ModifierType;
  currency?: SupportedPaymentCurrency;
  disabled?: boolean;
  isRequired?: boolean;
}

export function RateModifierInput({
  modifierType,
  modifierValue,
  updateDetails,
  type,
  currency,
  disabled = false,
  isRequired = true,
}: Props) {
  const { t } = useTranslation();

  const percentageTypes: string[] = [
    TaxAmountType.TaxAmountTypePercentage,
    MarkupAmountType.MarkupAmountTypePercentage,
    AdminFeeAmountType.AdminFeeAmountTypePercentage,
  ];

  const isTypePercentage =
    modifierType && percentageTypes.includes(modifierType);

  const isTypeTax = type === ModifierType.TAX || type === ModifierType.ADMIN_FEE_TAX;

  const getInputIcon = () => {
    if (isTypePercentage) {
      return 'percentage';
    }

    return '';
  };

  const handleInputChange = (value: string) => {
    if (type === ModifierType.RATE) {
      updateDetails(value, type);
    } else {
      updateDetails({ value, type: modifierType }, type);
    }
  };

  const inputError = () => {
    if (modifierValue) {
      if (isTypeTax && isTypePercentage) {
        if (!isNumberThreeDecimalPlaces(parseFloat(modifierValue))) {
          return [t('abp.productsServices.createProduct.max3Decimal')];
        }
      } else if (!isNumberTwoDecimalPlaces(parseFloat(modifierValue))) {
        return [t('abp.productsServices.createProduct.max2Decimal')];
      }
    }

    return null;
  };

  return (
    <div className={styles.additionalRateGroup}>
      {type !== ModifierType.RATE && (
        <Dropdown
          label={t(`abp.productsServices.createProduct.${type}.label`)}
          fixedLabel
          isRequired={isRequired}
          testId={`${type}ModifierDropdown`}
          className={styles.additionalRateType}
          value={modifierType}
          items={DROPDOWN_OPTIONS[type]}
          onChange={item => {
            updateDetails({ value: modifierValue, type: item.value }, type);
          }}
          disabled={disabled}
        />
      )}
      <Input
        label={t('abp.productsServices.createProduct.value')}
        fixedLabel
        isRequired={isRequired}
        testId={`${type}ValueInput`}
        className={styles.additionalRateValue}
        showClear={false}
        icon={getInputIcon()}
        input={{ type: 'number', step: '0.01', min: 0.0 }}
        iconRight
        value={modifierValue}
        onChange={handleInputChange}
        error={inputError()}
        onKeyDown={e => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        }}
        onWheel={e => {
          e.currentTarget.blur();
        }}
        disabled={disabled}
      />
      {currency && !isTypePercentage && (
        <div className={styles.currencyText}>{currency}</div>
      )}
    </div>
  );
}
